import { createStringSetCache } from './create-string-set-cache';

/**
 * Module-level memory of which analytics event "keys" have already been fired, so
 * a given event is emitted at most once per key for the lifetime of the page
 * (across component remounts).
 *
 * Why this exists:
 * Link-list components are re-mounted very frequently while content streams in
 * (for example the inline Lumina link list is re-rendered on every streamed chunk,
 * and the Rovo Extension chat panel re-mounts its tree as the answer streams). A
 * component-scoped guard (ref/state) resets on every remount and would still fire
 * duplicate events, so the guard must outlive the component instance. Keying by a
 * caller-provided string means each logical occurrence reports exactly once, no
 * matter how many times its component remounts.
 *
 * Callers are responsible for building a stable, collision-resistant key (e.g.
 * `knowledge-card-link-list::errored::<sorted urls>`).
 *
 * Implementation, memory bound and SSR safety are provided by the shared
 * {@link createStringSetCache} primitive.
 */
const cache = createStringSetCache();

/**
 * Returns whether an event has already been fired for the given key. Always
 * returns `false` on the server, or when `key` is undefined.
 */
export const hasEventFired = (key: string | undefined): boolean => cache.has(key);

/**
 * Records that an event has been fired for the given key. No-op on the server or
 * when `key` is undefined.
 */
export const markEventFired = (key: string | undefined): void => cache.mark(key);

/**
 * Internal reset used only to clear the module-level cache.
 *
 * Intentionally named with an `__unsafe` prefix and only meant to be consumed via
 * `fired-event-cache.test-utils.ts` so it cannot leak into production code paths,
 * where flushing the cache mid-session would reintroduce duplicate events.
 */
export const __unsafeResetFiredEventCache = (): void => cache.__unsafeReset();
